<script setup>

import Table from "@/components/common/other/Table";
import StaticWidget from "@/components/common/other/StaticWidget";
import StaticTooltip from "@/components/common/other/StaticTooltip";
import SkuHistorySearchPillGroup from "@/components/warehouse/SkuHistorySearchPillGroup";
import SkuHistoryNameGroupList from "@/components/warehouse/SkuHistoryNameGroupList";
import SkuHistoryCompositesList from "@/components/warehouse/SkuHistoryCompositesList";
import Button from "@/components/common/input/Button";
import BusySpinner from "@/components/common/other/BusySpinner.vue";
import SkuHistorySearch from "@/classes/stock/SkuHistorySearch";

import {inject, computed, onMounted} from 'vue'
import {useStore} from 'vuex';
import {useRouter} from "vue-router";

import API from "@/classes/global/API";
import {useDownloadLocalFile} from "@/composables/common/useDownloadLocalFile";
import Notification from "@/classes/component-classes/Notification";

const store = useStore();
const skuHistory = inject('skuHistory');
const router = useRouter();

const props = defineProps({

  modelValue: {}
})

onMounted(() => {
//  enable/disbale currency/value columns per account setting

  if(store.state.userSettings.showStockValues) {

    skuHistory.value.skuCompositeGridCols.find(x => x.id ==='10').enabled = true;
    skuHistory.value.skuCompositeGridCols.find(x => x.id ==='11').enabled = true;

  }

getSkuNames();

})

const gridHeight = computed(() => {

  // Adjust grid height when small screen layout is applied
  if(props.modelValue <= 1679) return'380';

  return '600'
});


async function getSkuNames() {

 const response = await API.getSkuHistoryGroupedNames();

 if(response) {

   skuHistory.value.skuNames = response;
 }

 let selectedName = skuHistory.value.skuNames.find(x => x.skuName === skuHistory.value.selectedSkuName);

  if(selectedName) return selectedName.active = true;

  // If new list does not contain the previous selected list item, clear all selections
  skuHistory.value.selectedSkuName = '';
  skuHistory.value.selectedSkuComposite = '';
  skuHistory.value.skuCompositeGridData = [];

  skuHistory.value.skuComposites.forEach(item => {

    item.active = false;

  });

}

async function downloadToExcel() {

  skuHistory.value.hideExcelDownloadBusy = false;

  const response = await API.skuHistoryExcelDownload(skuHistory.value.searchCriteria);

  if(response) {

    try {

      useDownloadLocalFile(response);

    } catch(error) {

      skuHistory.value.hideExcelDownloadBusy = true;
      store.state.banners.error = new Notification(true, 'Could Not Process The File');
    }

  }

  skuHistory.value.hideExcelDownloadBusy = true;

}

function getJobDetails(row) {

  store.state.hideLoadingSpinner = false;

  try {

    router.push({ name: 'viewshipment', params: { owner: store.state.owner, mode: row.mode, id: row.shipmentId }})

  } catch (error) {

    store.state.hideLoadingSpinner = true;
  }
}

function handleClearList() {

  skuHistory.value.selectedSkuName = '';
  skuHistory.value.selectedSkuComposite = '';
  skuHistory.value.skuComposites = [];
  skuHistory.value.skuCompositeGridData = [];

  getSkuNames();
}

function clearAllSearches() {

  skuHistory.value.searchCriteria = new SkuHistorySearch();

  handleClearList();

  getSkuNames();
}

</script>

<template class="cmp-sku-history">
  <div class="cmp-sku-history_wrapper">
    <StaticWidget title="Search SKU History" height="735">
      <template #extra-content>
<!--        <div v-if="skuHistory.selectedSkuName" class="cmp-sku-history_selected-sku-name" style="margin-left: 40px">{{skuHistory.selectedSkuName}}</div>-->
<!--        <i v-if="skuHistory.selectedSkuComposite" class="fa-duotone fa-circle-arrow-right" style="margin-top: 8px; color: #0e934b"></i>-->
<!--        <div v-if="skuHistory.selectedSkuComposite" class="cmp-sku-history_selected-sku-composite">{{skuHistory.selectedSkuComposite}}</div>-->
      </template>
      <template #right-side>
        <BusySpinner v-if="!skuHistory.hideExcelDownloadBusy" :hide="skuHistory.hideExcelDownloadBusy" message="Downloading Data..."></BusySpinner>
        <Button
            v-if="skuHistory.hideExcelDownloadBusy"
            data-cy="btnExcelDownload"
            @click="downloadToExcel()"
            :disabled="skuHistory.skuCompositeGridData.length === 0"
            text="Export"
            title="Export Data"
            icon='<i class="fa-solid fa-file-excel"></i>'
            type="success"
            style="margin-right: 0px;">
        </Button>
      </template>
      <SkuHistorySearchPillGroup
          @clear="handleClearList()"
          @clear-all="clearAllSearches"
          v-model="skuHistory">
      </SkuHistorySearchPillGroup>
      <div class="cmp-sku-history_select-items-area">
        <div class="cmp-sku-history_item-group-list-container">
          <div class="cmp-sku-history_sku-name-heading">SKU Names
            <StaticTooltip :icon="false">
              <template #content>
                <div style="width: 300px">
                  Select a SKU name to view the list of SKU composites associated with it.
                  These will show in the next section to the right
                </div>
              </template>
              <i class="fa-solid fa-circle-info icon-info"></i>
            </StaticTooltip>
          </div>
              <div class="cmp-sku-history_item-group-list">
            <SkuHistoryNameGroupList
                v-model="skuHistory">
            </SkuHistoryNameGroupList>
          </div>
        </div>
        <div class="cmp-sku-history_item-sku-group-list-container">
          <div class="cmp-sku-history_sku-composite-heading">SKU Composites
            <StaticTooltip :icon="false">
              <template #content>
                <div style="width: 280px">
                  Select a SKU composite to view it's history of usage & movement.
                  Details will populate in the grid.
                </div>
              </template>
              <i class="fa-solid fa-circle-info icon-info"></i>
            </StaticTooltip>
          </div>
          <div class="cmp-sku-history_item-sku-composite-list">
            <SkuHistoryCompositesList
                v-model="skuHistory">
            </SkuHistoryCompositesList>
          </div>
        </div>
        <div class="cmp-sku-history_item-grid">
          <Table
              :allowGridMenu="false"
              :height="gridHeight"
              :selectable="false"
              :row-selection="false"
              :data-source="skuHistory.skuCompositeGridData"
              :columns="skuHistory.skuCompositeGridCols">
            <template v-slot:whRef="row">
              <div class="cmp-sku-history_warehouse-ref-link"><u>{{row.rowData.whRef}}</u></div>
            </template>
            <template v-slot:shipmentNumber="row">
              <div @click="getJobDetails(row.rowData)" class="cmp-sku-history_warehouse-ref-link"><u>{{row.rowData.shipmentNumber}}</u></div>
            </template>
          </Table>
        </div>
      </div>
    </StaticWidget>
  </div>
</template>

<style lang="scss" scoped>

.cmp-sku-history {

  &_search-area {
    position: relative;
    height: 120px;
    gap: 20px;
    width: 1820px;
    background-color: var(--body-bg);
    border-radius: var(--rounded-corners);
    border: 1px solid var(--border-color);
  }

  &_search-area-icon {
    position: absolute;
    top: 40px;
    left: 30px;
    height: 120px;
  }

  &_search-fields {
    display: flex;
    padding-top: 30px;
    margin-left: 100px;
    gap: 20px;
  }

  &_select-items-area {
    //background-color: #f4f4f4;
    background-image: linear-gradient(0deg, #f3f3f3, #ffffff);
    display: grid;
    grid-template-columns: 1fr 1fr 1fr;
    margin-top: 10px;
    gap:10px;
    height: max-content;
    overflow-y: auto;
  }

  &_item-group-list-container, &_item-sku-group-list-container {
    min-width: -webkit-fill-available;
    background-color: #ffffff;
    height: 650px;
    border-radius: 4px;
    border: 1px solid var(--border-color);
    //box-shadow: 0px 1px 4px 0px #bebebe;
    //background-image: linear-gradient(0deg, #f3f3f3, #ffffff);
  }

  &_item-group-list, &_item-sku-composite-list {
    padding: 5px;
  }

  &_sku-name-heading, &_sku-composite-heading {
    font-size: 12px;
    font-weight: bold;
    padding: 5px 10px 6px 10px;
    color: #353535;
    border-bottom: 1px solid #f3f3f3;
    background-image: linear-gradient(0deg, #eaeaea, #fff);
  }

  &_sku-composite-heading {
    background-color: #fdfbf6;
  }

  &_sub-heading {
    font-size: 11px;
    color: #757575;
    margin-left: 10px;
  }

  &_warehouse-ref-link {
    text-overflow: ellipsis;
    overflow: hidden;
    white-space: nowrap;
    color: #1d6492;
    cursor: pointer;
  }

  &_selected-sku-name, &_selected-sku-composite {
    font-weight: normal;
    font-size: 11px;
    background-color: #fdfdfd;
    border-radius: 8px;
    padding-left:  10px;
    padding-right: 10px;
    color: #494949;
    box-shadow: 0px 1px 4px 0px #bebebe;
  }

  @media screen and (max-width: 1679px) {

    &_item-group-list-container, &_item-sku-group-list-container {
      background-color: #ffffff;
      height: 200px;
      border-radius: 2px;
      border: 1px solid var(--border-color);
      //box-shadow: 0px 1px 4px 0px #bebebe;
      //background-image: linear-gradient(0deg, #f3f3f3, #ffffff);
    }

    &_select-items-area {
      background-color: #f4f4f4;
      //background-image: linear-gradient(0deg, #f3f3f3, #ffffff);
      display: grid;
      grid-template-columns: 545px 545px;
      margin-top: 10px;
      gap:10px;
      height: 630px;
      overflow-x: auto;
      overflow-y: hidden;
    }

  }

  @media screen and (max-width: 1200px) {

    &_selected-sku-name, &_selected-sku-composite {
      display: none;
    }

  }

}
</style>
